import React from "react";
import { useState } from "react";
import * as ManagedServiceStore from "../ManagedServiceStore";
import { ScadsysManagedService, ManagedServiceType, ScadsysConfiguration } from "../ManagedServiceStore";
import { ConfigFormField } from "../../organizations/OrganizationManagedServices";
import SquareButton from "../../general/SquareButton";
import { Button, Modal } from "../../components/Modal";
import { set } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "../../general/HttpClient";

interface ScadsysManagedServiceProps {
    organizationId: string;
    onDone: () => void;
}

interface RenderScadsysInstanceProps {
    managedService: ScadsysManagedService;
    onChange?: () => void;
}

interface StorageStatusCheck {
    name: string;
    status: boolean;
    message: string;
    details?: Record<string, string>;
}

interface StorageStatus {
    overallStatus: string;
    configurationPercentage: number;
    checks: StorageStatusCheck[];
}

export const ScadsysManagedService = ({ organizationId, onDone }: ScadsysManagedServiceProps) => {
    const setConfig = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const newConfig = { ...newManagedService.configuration };
        set(newConfig, key, e.target.value);
        setNewManagedService({ ...newManagedService, configuration: newConfig });
    };

    const BoundConfigFormField = (key: string, name: string) => {
        return ConfigFormField(key, name, newManagedService.configuration, setConfig);
    };

    const [showModal, setShowModal] = useState(false);
    const [errors, setErrors] = useState<Array<{ message: string }>>([]);

    const [newManagedService, setNewManagedService] = useState<Partial<ScadsysManagedService>>({
        type: ManagedServiceType.Scadsys,
        organizationID: organizationId,
        configuration: {
            webServiceUrl: "",
            webServiceReference: "",
            onlineSchoolID: "",
        },
    });

    const start = () => {
        setShowModal(true);
        setNewManagedService({
            type: ManagedServiceType.Scadsys,
            organizationID: organizationId,
            configuration: {
                webServiceUrl: "",
                webServiceReference: "",
                onlineSchoolID: "",
            },
        });
    };

    const complete = () => {
        ManagedServiceStore.CreateManagedService<ScadsysConfiguration>(organizationId, newManagedService)
            .then((response) => {
                setErrors([]);
                setShowModal(false);
                onDone();
            })
            .catch((error) => {
                if (error.response?.data?.errors && error.response.data.errors.length > 0) {
                    setErrors(error.response.data.errors);
                }
            });
    };

    return (
        <>
            <>
                <SquareButton className="col" onClick={start}>
                    Scadsys
                    <br />
                    {/* @ts-ignore */}
                    <FontAwesomeIcon icon={["fad", "circle-plus"]} size="lg" className="text-blue-600"></FontAwesomeIcon>
                </SquareButton>
            </>
            <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="New Scadsys Managed Service" icon={["fad", "school"]} iconColor="text-blue-600" size="lg">
                <div className="p-6">
                    {BoundConfigFormField("onlineSchoolID", "Online School ID")}
                    {BoundConfigFormField("webServiceUrl", "Web Service URL")}
                    {BoundConfigFormField("webServiceReference", "Web Service Reference")}

                    {errors.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                            {errors.map((error, i) => (
                                <p key={i} className="text-red-800 text-sm">
                                    {error.message}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={complete}>
                        Create
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export const RenderScadsysRow = (configuration: ScadsysConfiguration) => {
    return (
        <>
            <td>Online School ID: {configuration.onlineSchoolID}</td>
            <td>Name: {configuration.institutionName}</td>
        </>
    );
};

export const RenderScadsysInstance = ({ managedService, onChange }: RenderScadsysInstanceProps) => {
    const [syncTimetables, setSyncTimetables] = React.useState<boolean>(managedService?.configuration?.syncTimetables || false);
    const [isInitializing, setIsInitializing] = React.useState(false);
    const [initializeMessage, setInitializeMessage] = React.useState<{ type: "success" | "error"; text: string } | null>(null);
    const [storageStatus, setStorageStatus] = React.useState<StorageStatus | null>(null);
    const [loadingStatus, setLoadingStatus] = React.useState(false);
    const [showStatusChecks, setShowStatusChecks] = React.useState(false);
    const isInitialMount = React.useRef(true);

    React.useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        updateManagedService();
    }, [syncTimetables]);

    React.useEffect(() => {
        loadStorageStatus();
    }, [managedService.organizationID]);

    const loadStorageStatus = async () => {
        setLoadingStatus(true);
        try {
            const response = await axios.get(`/Azure/EvaluateOrganizationStorage/${managedService.organizationID}`);
            setStorageStatus(response.data);
        } catch (error) {
            console.error("Failed to load storage status", error);
        } finally {
            setLoadingStatus(false);
        }
    };

    const updateManagedService = () => {
        const updatedService: ScadsysManagedService = {
            ...managedService,
            configuration: { ...managedService.configuration, syncTimetables },
        };
        ManagedServiceStore.UpdateManagedService<ScadsysConfiguration>(managedService.organizationID, updatedService).then(() => {
            onChange && onChange();
        });
    };

    const handleInitializeOrganization = async () => {
        setIsInitializing(true);
        setInitializeMessage(null);

        try {
            const response = await axios.post(`/Azure/InitializeOrganizationStorage/${managedService.organizationID}`);
            setInitializeMessage({ type: "success", text: response.data.message || "Organization storage initialized successfully" });
            // Refresh the managed service to get updated configuration
            onChange && onChange();
            // Reload storage status
            loadStorageStatus();
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message || "Failed to initialize organization storage";
            setInitializeMessage({ type: "error", text: errorMessage });
        } finally {
            setIsInitializing(false);
        }
    };

    return (
        <>
            <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                    <FontAwesomeIcon icon={["fad", "school"]} className="text-white text-lg" />
                </div>
                <div>
                    <h4 className="text-lg font-semibold text-gray-900">Scadsys</h4>
                    <p className="text-sm text-gray-500">School Management System</p>
                </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <h5 className="font-medium text-gray-800 mb-3 flex items-center">
                    <FontAwesomeIcon icon={["fad", "server"]} className="text-purple-600 mr-2" />
                    Service Configuration
                </h5>
                <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Service URL</p>
                            <p className="text-sm font-mono text-gray-900 mt-1 break-all">{managedService.configuration.webServiceUrl || "—"}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Reference</p>
                            <p className="text-sm text-gray-900 mt-1">{managedService.configuration.webServiceReference || "—"}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Institution Name</p>
                            <p className="text-sm text-gray-900 mt-1">{managedService.configuration.institutionName || "—"}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Online School ID</p>
                            <p className="text-sm text-gray-900 mt-1">{managedService.configuration.onlineSchoolID || "—"}</p>
                        </div>
                    </div>
                </div>
            </div>

            {(managedService.configuration.storageAccountName || managedService.configuration.fileSystemName || managedService.configuration.organizationFolder) && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                        <h5 className="font-medium text-blue-800 flex items-center">
                            <FontAwesomeIcon icon={["fad", "database"]} className="text-blue-600 mr-2" />
                            Data Lake Hierarchy Configuration
                        </h5>
                        <div className="flex items-center gap-2">
                            {loadingStatus && <FontAwesomeIcon icon={["fad", "spinner"]} spin className="text-blue-600" />}
                            {storageStatus && !loadingStatus && (
                                <span
                                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                        storageStatus.overallStatus === "Fully Configured"
                                            ? "bg-green-100 text-green-800"
                                            : storageStatus.overallStatus === "Partially Configured"
                                            ? "bg-yellow-100 text-yellow-800"
                                            : "bg-red-100 text-red-800"
                                    }`}
                                >
                                    {storageStatus.configurationPercentage}% Configured
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs font-medium text-blue-600 uppercase">Storage Account</p>
                                <p className="text-sm font-mono text-gray-900 mt-1">{managedService.configuration.storageAccountName || "—"}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-blue-600 uppercase">File System</p>
                                <p className="text-sm font-mono text-gray-900 mt-1">{managedService.configuration.fileSystemName || "—"}</p>
                            </div>
                            <div className="md:col-span-2">
                                <p className="text-xs font-medium text-blue-600 uppercase">Organization Folder</p>
                                <p className="text-sm font-mono text-gray-900 mt-1">{managedService.configuration.organizationFolder || "—"}</p>
                            </div>
                        </div>
                        {managedService.configuration.tenantId && (
                            <div className="pt-3 border-t border-blue-200">
                                <p className="text-xs font-medium text-blue-600 uppercase">Tenant ID</p>
                                <p className="text-sm font-mono text-gray-900 mt-1">{managedService.configuration.tenantId}</p>
                            </div>
                        )}
                        {managedService.configuration.servicePrincipalAppId && (
                            <div className="pt-3 border-t border-blue-200">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs font-medium text-blue-600 uppercase">Service Principal App ID</p>
                                        <p className="text-sm font-mono text-gray-900 mt-1">{managedService.configuration.servicePrincipalAppId}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-blue-600 uppercase">Service Principal ID</p>
                                        <p className="text-sm font-mono text-gray-900 mt-1">{managedService.configuration.servicePrincipalId || "—"}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {storageStatus && storageStatus.checks && storageStatus.checks.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-blue-200">
                            <button
                                onClick={() => setShowStatusChecks(!showStatusChecks)}
                                className="w-full flex items-center justify-between text-left hover:bg-blue-100 rounded p-2 transition-colors"
                            >
                                <h6 className="text-xs font-semibold text-blue-800 uppercase">Setup Status Checks</h6>
                                <FontAwesomeIcon icon={["fad", showStatusChecks ? "chevron-up" : "chevron-down"]} className="text-blue-600 text-sm" />
                            </button>
                            {showStatusChecks && (
                                <div className="space-y-2 mt-2 animate-in slide-in-from-top-2">
                                    {storageStatus.checks.map((check: any, index: number) => (
                                        <div key={index} className="flex items-start gap-2 text-sm bg-white rounded p-2 border border-blue-100">
                                            <FontAwesomeIcon icon={["fad", check.status ? "check-circle" : "times-circle"]} className={`mt-0.5 ${check.status ? "text-green-600" : "text-red-600"}`} />
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between">
                                                    <span className={`font-medium ${check.status ? "text-green-800" : "text-red-800"}`}>{check.name}</span>
                                                </div>
                                                <p className="text-xs text-gray-600 mt-0.5">{check.message}</p>
                                                {check.details && (
                                                    <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                                                        {Object.entries(check.details).map(([key, value]: [string, any]) => (
                                                            <div key={key}>
                                                                <span className="font-medium">{key}:</span> {value}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {loadingStatus && !storageStatus && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
                    <div className="flex flex-col items-center justify-center">
                        <FontAwesomeIcon icon={["fad", "spinner"]} spin className="text-blue-600 text-3xl mb-3" />
                        <p className="text-blue-800 font-medium">Evaluating storage configuration...</p>
                        <p className="text-blue-600 text-sm mt-1">Please wait while we check your setup</p>
                    </div>
                </div>
            )}

            {storageStatus && storageStatus.configurationPercentage < 100 && (
                <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-lg p-5 mb-4 shadow-sm">
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-200 flex items-center justify-center">
                            <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-yellow-700 text-lg" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                                <h5 className="font-semibold text-yellow-900 text-lg">
                                    {storageStatus.configurationPercentage === 0 ? "Data Lake Storage Not Configured" : "Data Lake Storage Partially Configured"}
                                </h5>
                                {storageStatus && !loadingStatus && (
                                    <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-yellow-200 text-yellow-900 border border-yellow-300">
                                        {storageStatus.configurationPercentage}% Complete
                                    </span>
                                )}
                            </div>
                            <p className="text-yellow-800 text-sm mb-4 leading-relaxed">
                                {storageStatus.configurationPercentage === 0
                                    ? "This organization's data lake storage has not been initialized. Click the button below to set up Azure Data Lake storage for this organization."
                                    : "This organization's data lake storage setup is incomplete. Click the button below to complete the configuration."}
                            </p>

                            {isInitializing && (
                                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-start gap-3">
                                        <FontAwesomeIcon icon={["fad", "spinner"]} spin className="text-blue-600 text-xl mt-1" />
                                        <div>
                                            <p className="text-blue-900 font-semibold mb-1">Initializing Storage...</p>
                                            <p className="text-blue-700 text-sm mb-2">Creating storage resources and configuring access. This process can take up to 2 minutes.</p>
                                            <div className="flex items-center gap-2 text-xs text-blue-600">
                                                <FontAwesomeIcon icon={["fad", "clock"]} />
                                                <span>Please do not close this window</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!isInitializing && (
                                <Button variant="primary" onClick={handleInitializeOrganization} disabled={isInitializing} className="shadow-md hover:shadow-lg transition-all">
                                    <FontAwesomeIcon icon={["fad", "play"]} className="mr-2" />
                                    Initialize Organization Storage
                                </Button>
                            )}

                            {initializeMessage && (
                                <div
                                    className={`mt-4 p-4 rounded-lg shadow-sm ${
                                        initializeMessage.type === "success" ? "bg-green-50 border-2 border-green-300 text-green-900" : "bg-red-50 border-2 border-red-300 text-red-900"
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <FontAwesomeIcon
                                            icon={["fad", initializeMessage.type === "success" ? "check-circle" : "times-circle"]}
                                            className={`text-xl mt-0.5 ${initializeMessage.type === "success" ? "text-green-600" : "text-red-600"}`}
                                        />
                                        <div>
                                            <p className="font-semibold mb-1">{initializeMessage.type === "success" ? "Initialization Complete" : "Initialization Failed"}</p>
                                            <p className="text-sm">{initializeMessage.text}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h5 className="font-medium text-blue-800 mb-3">Configuration Options</h5>
                <div>
                    <label htmlFor="syncTimetables" className="flex items-center">
                        <input type="checkbox" id="syncTimetables" checked={syncTimetables} onChange={(e) => setSyncTimetables((e.target as HTMLInputElement).checked)} className="mr-2" />
                        <span className="text-blue-800">Sync Scadsys Timetables</span>
                    </label>
                    <p className="text-blue-600 text-xs mt-1 ml-6">Enable automatic synchronization of timetable data from Scadsys</p>
                </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                    <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-600 mr-2" />
                    <span className="text-green-800 font-medium">Service Active</span>
                </div>
                <p className="text-green-700 text-sm mt-1">Scadsys integration is configured and ready for data synchronization.</p>
            </div>
        </>
    );
};
